import { useCallback, useRef } from 'react';

/**
 * useZoomKeepsCentre(scrollRef, zoom, dayWidth, setZoom) → { zoomTo, applyCentre }
 *
 * THE answer to "what stays put when a Gantt's zoom changes": the DATE in the
 * middle of the visible chart (owner 2026-10-13: "when we change the zoom
 * (week, month, quarter) we centralise the current view rather than zooming
 * based on the date line"). Every Gantt used to snap back to today on a zoom
 * change, which threw away wherever the user had scrolled to.
 *
 *   zoomTo(next)    the zoom buttons call this instead of `setZoom`. It notes
 *                   the centre of the view, in DAYS from the axis start, at the
 *                   OLD day width, then sets the zoom.
 *   applyCentre()   the chart's own "position the scroll" effect calls this
 *                   FIRST. It answers true when it has placed the scroll for a
 *                   zoom change (the caller must then skip its snap to today),
 *                   false when there is nothing pending (first paint, another
 *                   department: the caller's own rule applies).
 *
 * The centre is noted at CLICK time, never read back in the effect: after the
 * re-render a narrower chart has already had its `scrollLeft` clamped by the
 * browser, so the old position is gone by then.
 *
 * A zoomed-out chart can be narrower than the view, or the centre can sit near
 * an end, so the browser cannot scroll far enough to put the date in the
 * middle. The INTENDED centre is remembered with the scroll position it
 * produced: if the user has not scrolled since, the next zoom change uses that
 * intent, so Month -> Quarter -> Month comes back to where it started.
 *
 * It assumes the axis START does not move with the zoom, true of all five
 * charts. Display only: it decides nothing and writes nothing but a scroll.
 * jsdom has no layout, so the tests pin the arithmetic and the wiring; that the
 * same dates are in the middle after a click is a BROWSER check.
 */
export default function useZoomKeepsCentre(scrollRef, zoom, dayWidth, setZoom) {
  const pendingCentreDays = useRef(null);
  const lastPlaced = useRef(null); // { left, centreDays }: what applyCentre last did
  const placement = useRef(0); // counts zoom clicks, so a stale re-assert stands down

  const zoomTo = useCallback((next) => {
    if (next === zoom) return;
    placement.current += 1;
    const el = scrollRef.current;
    if (!el || !(dayWidth > 0)) pendingCentreDays.current = null;
    else if (lastPlaced.current && Math.abs(el.scrollLeft - lastPlaced.current.left) < 1) pendingCentreDays.current = lastPlaced.current.centreDays;
    else pendingCentreDays.current = (el.scrollLeft + el.clientWidth / 2) / dayWidth;
    setZoom(next);
  }, [scrollRef, zoom, dayWidth, setZoom]);

  const applyCentre = useCallback(() => {
    const el = scrollRef.current;
    const centreDays = pendingCentreDays.current;
    if (!el || centreDays == null) return false;
    pendingCentreDays.current = null;
    const target = Math.max(0, centreDays * dayWidth - el.clientWidth / 2);
    const place = () => {
      el.scrollLeft = target;
      lastPlaced.current = { left: el.scrollLeft, centreDays }; // read back: the browser may have clamped it
    };
    place();
    // A page-scroll Gantt links this pane two ways to a sticky scrollbar whose
    // content width is MEASURED (a ResizeObserver, then a render). Until that
    // lands the bar still has the old zoom's range, clamps the new position and
    // echoes its clamped value back here (browser-measured 2026-10-13: three of
    // five charts did not move at all). So the placement is re-asserted once the
    // bar has caught up, unless the user has ZOOMED again meanwhile. It cannot
    // tell a user's scroll from the bar's echo, so a pan in those ~0.2s is
    // overwritten: a known, accepted cost (review F3, 2026-10-13).
    const turn = (placement.current += 1);
    const again = () => {
      if (placement.current !== turn || !el.isConnected) return false;
      place();
      return true;
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => { if (again()) requestAnimationFrame(() => { if (again()) setTimeout(again, 120); }); });
    }
    return true;
  }, [scrollRef, dayWidth]);

  return { zoomTo, applyCentre };
}
